import { Injectable, effect, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, firstValueFrom, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LANGUAGES, Language, localeForLang, setActiveLocale } from '@shared/utils/i18n';
import { UserPreferencesService, UserPreferences } from './user-preferences.service';

/** Idiomas efectivamente soportados por la app (deben existir en assets/i18n/{code}.json). */
export const SUPPORTED_LANGS = ['es', 'en', 'pt'] as const;

/** Clave de localStorage donde se recuerda el último idioma elegido (para el primer render y el LOCALE_ID). */
export const LANG_STORAGE_KEY = 'app_lang';

/**
 * Fuente de verdad del idioma de la interfaz.
 *
 * Precedencia (espeja la de zona horaria, ver TimezoneService): preferencia del usuario
 * (`UserPreferences.language`) → último idioma recordado (localStorage) → default de Transloco (`es`).
 *
 * Cambia el idioma EN CALIENTE vía Transloco y persiste la elección en las preferencias del
 * usuario y en localStorage. El formateo de fechas/números (LOCALE_ID) se resuelve al arranque
 * desde localStorage; su cambio reactivo sin recargar es una fase posterior del roadmap i18n.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly prefsService = inject(UserPreferencesService);

  /** Idiomas disponibles para la UI (intersección de LANGUAGES con los soportados). */
  readonly available: Language[] = LANGUAGES.filter(l =>
    (SUPPORTED_LANGS as readonly string[]).includes(l.code)
  );

  /** Idioma activo actual, como signal para la UI. */
  readonly current = signal<string>(this.transloco.getActiveLang());

  constructor() {
    // Cuando cargan/actualizan las preferencias del usuario, el idioma EFECTIVO de la cuenta
    // manda. El backend resuelve la precedencia usuario → organización → default y lo entrega
    // en `effectiveLanguage`; si el usuario no tiene idioma personal (null = heredar), así se
    // aplica el idioma por defecto de su organización. `language` queda como respaldo.
    effect(() => {
      const prefs = this.prefsService.preferences();
      const lang = prefs?.effectiveLanguage ?? prefs?.language;
      if (lang && this.isSupported(lang)) {
        this.apply(lang);
      }
    });
  }

  /**
   * Preload del idioma recordado ANTES del primer render (lo llama un APP_INITIALIZER).
   * Garantiza que el bundle de traducción esté en memoria, de modo que las llamadas SÍNCRONAS
   * a `translate()` (cabeceras de AG-Grid, etc.) no devuelvan "missing" mientras viaja el JSON.
   * También deja fijado el locale de formato (Fase 3) desde el arranque.
   */
  init(): Promise<void> {
    const lang = this.readStored() ?? this.transloco.getActiveLang();
    return firstValueFrom(this.transloco.load(lang)).then(() => this.activate(lang));
  }

  /**
   * Fija un idioma PERSONAL explícito (en caliente) y lo persiste.
   * A diferencia de heredar, este valor se respeta aunque cambie el default de la organización.
   * Devuelve el observable del guardado para que el llamador gestione el estado (p. ej. spinner).
   */
  use(lang: string): Observable<UserPreferences | null> {
    if (!this.isSupported(lang)) {
      return of(null);
    }
    this.apply(lang);

    const base = this.prefsService.preferences();
    // Si ya está fijado explícitamente a este idioma, no repetimos el guardado.
    if (base?.language === lang) {
      return of(base);
    }
    const updated: UserPreferences = {
      ...(base ?? { theme: 'light', language: lang, notificationsEnabled: true }),
      language: lang,
    };
    return this.prefsService.updatePreferences(updated);
  }

  /**
   * Vuelve a HEREDAR el idioma de la organización (borra la preferencia personal).
   * Envía cadena vacía como sentinel; el backend la interpreta como "poner a null" y
   * devuelve el `effectiveLanguage` resuelto (el de la organización), que aplicamos en caliente.
   */
  useInherit(): Observable<UserPreferences | null> {
    const base = this.prefsService.preferences();
    const updated: UserPreferences = {
      ...(base ?? { theme: 'light', language: '', notificationsEnabled: true }),
      language: '',
    };
    return this.prefsService.updatePreferences(updated).pipe(
      tap((p) => {
        const eff = p?.effectiveLanguage ?? undefined;
        if (this.isSupported(eff)) {
          this.apply(eff);
        }
      }),
    );
  }

  isSupported(lang: string | null | undefined): lang is string {
    return !!lang && (SUPPORTED_LANGS as readonly string[]).includes(lang);
  }

  /**
   * Aplica un idioma en caliente. Carga el bundle ANTES de activarlo (load-then-activate) para
   * que el cambio no provoque "missing translation" en las traducciones síncronas mientras el
   * JSON viaja por HTTP. `load()` emite al instante si el bundle ya está en caché.
   */
  private apply(lang: string): void {
    this.transloco.load(lang).subscribe(() => this.activate(lang));
  }

  /** Activa el idioma ya cargado: Transloco + signal + localStorage + locale de formato (Fase 3). */
  private activate(lang: string): void {
    // El locale de formato se fija ANTES de anunciar el idioma: `setActiveLang` emite langChanges$
    // de forma síncrona y sus suscriptores formatean fechas/números leyendo `getActiveLocale()`.
    // Si se fijara después, esos suscriptores renderizarían una vez con el locale anterior.
    setActiveLocale(localeForLang(lang));
    if (this.transloco.getActiveLang() !== lang) {
      this.transloco.setActiveLang(lang);
    }
    this.current.set(lang);
    this.writeStored(lang);
    // Lectores de pantalla y traductores del navegador usan el idioma del documento.
    document.documentElement.lang = lang;
  }

  private readStored(): string | null {
    try {
      const v = localStorage.getItem(LANG_STORAGE_KEY);
      return this.isSupported(v) ? v : null;
    } catch {
      return null;
    }
  }

  private writeStored(lang: string): void {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      /* localStorage no disponible — ignorar */
    }
  }
}
